// 2D force-directed layout (Fruchterman-Reingold) for the KG canvas.
// Ported 1:1 from apps/metaphysics/frontend/src/views/mp-knowledge-graphs-view.ts
// so the visual feel of the graph is preserved.

export interface IGraphNode {
  id: string;
  label: string;
  entityType: string;
  description: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
  glowColor: string;
  pinned: boolean;
}

export interface IGraphEdge {
  source: string;
  target: string;
  description: string;
}

interface IForceOpts {
  k?: number;
  repulsion?: number;
  damping?: number;
  center?: number;
  edgeStrength?: number;
  maxVel?: number;
}

export class ForceSim2D {
  nodes: IGraphNode[];
  edges: IGraphEdge[];
  alpha = 1.0;
  private index: Map<string, number>;

  private readonly k: number;
  private readonly rep: number;
  private readonly damp: number;
  private readonly center: number;
  private readonly es: number;
  private readonly mv: number;

  constructor(
    nodes: IGraphNode[],
    edges: IGraphEdge[],
    width: number,
    height: number,
    opts: IForceOpts = {},
  ) {
    this.k = opts.k ?? 55;
    this.rep = opts.repulsion ?? 3200;
    this.damp = opts.damping ?? 0.88;
    this.center = opts.center ?? 0.0004;
    this.es = opts.edgeStrength ?? 0.04;
    this.mv = opts.maxVel ?? 8.0;
    this.nodes = nodes;
    this.edges = edges;
    this.index = new Map(nodes.map((n, i) => [n.id, i]));

    const R = Math.min(width, height) * 0.35;
    const cx = width / 2;
    const cy = height / 2;
    const N = nodes.length;
    for (let i = 0; i < N; i++) {
      const a = (i / N) * 2 * Math.PI;
      const r = R * (0.4 + 0.6 * Math.sqrt((i + 0.5) / N));
      const node = nodes[i];
      if (!node) continue;
      node.x = cx + r * Math.cos(a) + (Math.random() - 0.5) * 20;
      node.y = cy + r * Math.sin(a) + (Math.random() - 0.5) * 20;
    }
  }

  step(cx: number, cy: number) {
    const a = this.alpha;
    const ns = this.nodes;
    const rep = this.rep * a;

    for (let i = 0; i < ns.length; i++) {
      const ni = ns[i];
      if (!ni || ni.pinned) continue;
      for (let j = i + 1; j < ns.length; j++) {
        const nj = ns[j];
        if (!nj) continue;
        let dx = ni.x - nj.x;
        let dy = ni.y - nj.y;
        const d2 = dx * dx + dy * dy + 0.01;
        const d = Math.sqrt(d2);
        const f = rep / d2;
        dx /= d;
        dy /= d;
        ni.vx += (dx * f) / (ni.radius * 0.5);
        ni.vy += (dy * f) / (ni.radius * 0.5);
        if (!nj.pinned) {
          nj.vx -= (dx * f) / (nj.radius * 0.5);
          nj.vy -= (dy * f) / (nj.radius * 0.5);
        }
      }
    }

    for (const e of this.edges) {
      const si = this.index.get(e.source);
      const ti = this.index.get(e.target);
      if (si == null || ti == null) continue;
      const s = ns[si];
      const t = ns[ti];
      if (!s || !t) continue;
      const dx = t.x - s.x;
      const dy = t.y - s.y;
      const d = Math.sqrt(dx * dx + dy * dy) + 0.0001;
      const f = (d - this.k) * this.es * a;
      const ux = dx / d;
      const uy = dy / d;
      if (!s.pinned) {
        s.vx += ux * f;
        s.vy += uy * f;
      }
      if (!t.pinned) {
        t.vx -= ux * f;
        t.vy -= uy * f;
      }
    }

    for (const p of ns) {
      if (!p || p.pinned) continue;
      p.vx += (cx - p.x) * this.center * a;
      p.vy += (cy - p.y) * this.center * a;
      p.vx *= this.damp;
      p.vy *= this.damp;
      const sp = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
      if (sp > this.mv) {
        const r = this.mv / sp;
        p.vx *= r;
        p.vy *= r;
      }
      p.x += p.vx;
      p.y += p.vy;
    }

    this.alpha *= 0.995;
    if (this.alpha < 0.04) this.alpha = 0.04;
  }

  nodeAt(x: number, y: number): IGraphNode | null {
    for (let i = this.nodes.length - 1; i >= 0; i--) {
      const n = this.nodes[i];
      if (!n) continue;
      const dx = x - n.x;
      const dy = y - n.y;
      if (dx * dx + dy * dy <= (n.radius + 4) * (n.radius + 4)) return n;
    }
    return null;
  }
}
